import React from 'react';
import Button from '../../button';
import Tooltip from '../../tooltips/Tooltips';
import { useScrollContext } from './ScrollableTableWrapper';

/**
 * Composant réutilisable pour les cellules de table
 * @param {React.ReactNode} children - Contenu de la cellule
 * @param {string} position - Position de la cellule: 'first', 'last', 'middle' (défaut: 'middle')
 * @param {string} className - Classes CSS additionnelles
 * @param {boolean} hasButton - Si true, la cellule contient un bouton
 * @param {string} buttonColor - Couleur du bouton (défaut: 'TABLE')
 * @param {string} buttonSize - Taille du bouton (défaut: 'TABLE')
 * @param {function} buttonOnClick - Fonction appelée au clic du bouton
 * @param {React.ReactNode} buttonContent - Contenu du bouton
 * @param {string} [buttonTooltip] - Texte du survol (optionnel)
 * @param {string} [tooltipPlacement] - Placement du tooltip (défaut: top)
 * @param {boolean} stickyLeft - Si true, la cellule est sticky à gauche
 * @param {boolean} isSelected - Si true, la cellule est sélectionnée (fond jaune)
 */
const TableCell = ({
  children,
  position = 'middle',
  className = '',
  hasButton = false,
  buttonColor = 'TABLE',
  buttonSize = 'TABLE',
  buttonOnClick,
  buttonContent,
  buttonTooltip,
  tooltipPlacement = 'top',
  stickyLeft = false,
  isSelected = false,
}) => {
  // Déterminer le padding selon la position
  const getPaddingClass = () => {
    const pyClass = 'py-5';
    if (position === 'first') return `${pyClass} pl-4 pr-2 text-gray-900`;
    if (position === 'last') return `${pyClass} pl-2 pr-3`;
    return `${pyClass} px-2`;
  };

  const { hasHorizontalScroll = false } = useScrollContext();
  const paddingClass = getPaddingClass();
  const baseClasses = `${paddingClass} ${className}`;
  const bgClass = stickyLeft ? (isSelected ? 'bg-yellow-50' : 'bg-white') : '';
  const stickyClasses = stickyLeft ? `sticky left-0 z-20 ${bgClass}` : '';
  const stickyStyle = stickyLeft && hasHorizontalScroll ? { boxShadow: '2px 0 0 0 rgba(243, 244, 246, 1)' } : {};

  // Si la cellule contient un bouton
  if (hasButton && buttonContent) {
    return (
      <td 
        className={`${baseClasses} ${stickyClasses}`} 
        style={stickyStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div 
          className="flex justify-end items-center"
          onClick={(e) => e.stopPropagation()}
        >
          {buttonTooltip ? (
            <Tooltip text={buttonTooltip} placement={tooltipPlacement}>
              <span className="inline-flex">
                <Button
                  color={buttonColor}
                  size={buttonSize}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (buttonOnClick) buttonOnClick(e);
                  }}
                >
                  {buttonContent}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              color={buttonColor}
              size={buttonSize}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (buttonOnClick) buttonOnClick(e);
              }}
            >
              {buttonContent}
            </Button>
          )}
        </div>
      </td>
    );
  }

  return <td className={`${baseClasses} ${stickyClasses}`} style={stickyStyle}>{children}</td>;
};

export default TableCell;
